/**
 * Module-level callback bridge for Surface Poly's per-modal undo/redo.
 *
 * The handler owns its segment history (handler-local refs — high
 * frequency mutation that should never trigger React re-renders). The
 * store's global `undo` / `redo` actions need to consult that history
 * BEFORE falling through to the global undo stack — otherwise Cmd-Z
 * during the modal would always pop a global entry instead of
 * removing the most recent segment.
 *
 * Shape mirrors `registerSnapRaycast` in `modeling/snap.ts`: handler
 * registers / unregisters its closures via `useEffect`; store calls
 * `tryX()` and treats `false` as "fall through to the global path".
 *
 * Lives in `viewport/` because the producer is a viewport handler;
 * `app/store.ts` imports `tryX` only — no React types crossing the
 * module boundary.
 */

let _undo: (() => boolean) | null = null;
let _redo: (() => boolean) | null = null;

/** Called by `SurfacePolyHandler`'s `useEffect` on mount / unmount. */
export function registerSurfacePolyUndoRedo(
  undo: (() => boolean) | null,
  redo: (() => boolean) | null,
): void {
  _undo = undo;
  _redo = redo;
}

/**
 * Returns true iff the active Surface Poly modal consumed the undo
 * (pop segment from undoStack). False indicates either no modal is
 * active or the undoStack was empty — caller should treat that as
 * "fall through to the global undo path".
 */
export function trySurfacePolyUndo(): boolean {
  return _undo?.() ?? false;
}

/** Mirror of `trySurfacePolyUndo` for the redo direction. */
export function trySurfacePolyRedo(): boolean {
  return _redo?.() ?? false;
}

// ---------------------------------------------------------------------------
// Hovered endpoint signal — handler → gizmo
// ---------------------------------------------------------------------------
//
// During the drawing stage with no drag in flight, the handler tracks
// which polyline endpoint (if any) the cursor is closest to so the gizmo
// can highlight it. Module-level slot rather than store state because the
// hover is recomputed on every pointermove (60Hz) and the gizmo is the
// only consumer — going through Zustand would force a re-render path
// that's pointless here. Mirrors `setLastSnapResult` in `modeling/snap.ts`.

export type SurfacePolyHoveredEndpoint = "head" | "tail" | null;

let _hovered: SurfacePolyHoveredEndpoint = null;

export function setSurfacePolyHoveredEndpoint(
  end: SurfacePolyHoveredEndpoint,
): void {
  _hovered = end;
}

export function getSurfacePolyHoveredEndpoint(): SurfacePolyHoveredEndpoint {
  return _hovered;
}
